/**
 * In-memory pt-BR seed data for the mock client. Realistic telecom / Vlocity
 * EPC catalog content so the console looks alive offline and in demos.
 */
import type {
  AgentPersona,
  AiModelSettings,
  AppUser,
  BillingPlan,
  BillingReferrals,
  BillingSubscription,
  Citation,
  Conversation,
  Deck,
  Document,
  ExamBoard,
  IngestionRun,
  KnowledgeArea,
  MetricsCost,
  ModerationSource,
  PlatformOverview,
  Profile,
  ProfileFeatures,
  QAInteraction,
  SkillCard,
  TaxonomyArea,
  TaxonomyNode,
  Training,
  WhatsAppGroup,
  WhatsAppLink,
} from './types';

const now = Date.now();
const secondsAgo = (s: number): string => new Date(now - s * 1_000).toISOString();
const hoursAgo = (h: number): string => new Date(now - h * 3_600_000).toISOString();
const daysAgo = (d: number): string => new Date(now - d * 86_400_000).toISOString();
const dayDate = (d: number): string => new Date(now - d * 86_400_000).toISOString().slice(0, 10);

/** Every feature off — the base a seed row turns its own switches on from. */
export const NO_FEATURES: ProfileFeatures = {
  vision: false,
  audio: false,
  imageGeneration: false,
  messageRelay: false,
  reminders: false,
  examBoards: false,
  attachments: false,
};

/**
 * The profile catalog, mirroring the rows migration 0046 seeds. A profile is one
 * administrable package of quotas, capabilities and taxonomy, so these numbers
 * are a STARTING POINT the console can edit, never a rule the frontend
 * re-implements.
 *
 * `userCount` is left at zero here and recomputed by the mock client from the
 * seeded accounts: a count that could drift from the user list is exactly the
 * number a deletion refusal quotes.
 *
 * A voz oficial segue a 0057: todo perfil aponta para um Agente padrão. A ∫hibΔ
 * nos perfis de adulto, a Marlena no Técnico, e cada dependente com a própria
 * (as professoras dos Fundamentais e a Cuidadora).
 */
export const seedProfiles: Profile[] = [
  profile('padrao', 'Padrão', 'Perfil de entrada: uma base pessoal e um grupo.', {
    privateQuota: 50,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true },
  }),
  profile('business', 'Business (empresas)', 'Empresas pequenas: um grupo por departamento, mesma base.', {
    groupLimit: 4,
    groupMode: 'all_members',
    privateQuota: 500,
    userPersonasLimit: 10,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true },
  }),
  profile('tecnico', 'Técnico', 'Salesforce, integrações, arquitetura e programação.', {
    privateQuota: 100,
    userPersonasLimit: 5,
    defaultPersonaId: 'analista-funcional',
    features: { vision: true, audio: true },
  }),
  profile('concursos', 'Concursos', 'Preparação para concursos públicos, por banca.', {
    privateQuota: 100,
    userPersonasLimit: 5,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true, examBoards: true },
  }),
  profile('vestibular', 'Vestibular e Ensino Médio', 'ENEM, Fuvest, Unicamp e o Ensino Médio.', {
    assignableTo: 'both',
    privateQuota: 100,
    userPersonasLimit: 5,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true, examBoards: true },
  }),
  profile('familia', 'Família', 'O responsável cadastra dependentes que estudam com o agente.', {
    privateQuota: 100,
    dependentsLimit: 5,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true, reminders: true },
  }),
  profile('cuidador', 'Cuidador', 'O responsável acompanha um assistido: lembretes e recados.', {
    privateQuota: 100,
    dependentsLimit: 3,
    defaultPersonaId: 'shiba',
    features: { vision: true, audio: true, reminders: true },
  }),
  profile('fundamental-1', 'Fundamental 1 (1º–5º ano)', 'Dependente em alfabetização e anos iniciais.', {
    assignableTo: 'dependent',
    groupLimit: 0,
    privateQuota: 0,
    userPersonasLimit: 0,
    defaultPersonaId: 'f1-professora',
    features: { vision: true, audio: true, reminders: true },
  }),
  profile('fundamental-2', 'Fundamental 2 (6º–9º ano)', 'Dependente nos anos finais do fundamental.', {
    assignableTo: 'dependent',
    groupLimit: 0,
    privateQuota: 0,
    userPersonasLimit: 0,
    defaultPersonaId: 'f2-portugues',
    features: { vision: true, audio: true, reminders: true },
  }),
  profile('assistido', 'Assistido (Cuidadora)', 'Dependente acompanhado: lembretes, recados e transcrições.', {
    assignableTo: 'dependent',
    groupLimit: 0,
    privateQuota: 0,
    userPersonasLimit: 0,
    defaultPersonaId: 'cuidadora',
    features: { vision: true, audio: true, reminders: true, messageRelay: true },
  }),
];

/**
 * O dicionário de bancas, na mesma ordem em que o servidor o versiona
 * (`agents/exam_boards.py`). Aqui vive só o par slug/rótulo: o texto que
 * instrui o modelo é do servidor, e uma cópia dele no console seria a primeira
 * coisa a ficar desatualizada.
 */
export const seedExamBoards: ExamBoard[] = [
  { slug: 'cebraspe', label: 'Cebraspe', profile: 'concursos' },
  { slug: 'fgv', label: 'FGV', profile: 'concursos' },
  { slug: 'fcc', label: 'FCC', profile: 'concursos' },
  { slug: 'vunesp', label: 'Vunesp', profile: 'concursos' },
  { slug: 'cesgranrio', label: 'Cesgranrio', profile: 'concursos' },
  { slug: 'ibfc', label: 'IBFC', profile: 'concursos' },
  { slug: 'aocp', label: 'AOCP', profile: 'concursos' },
  { slug: 'quadrix', label: 'Quadrix', profile: 'concursos' },
  { slug: 'enem', label: 'ENEM', profile: 'vestibular' },
  { slug: 'fuvest', label: 'Fuvest', profile: 'vestibular' },
  { slug: 'unicamp', label: 'Unicamp', profile: 'vestibular' },
  { slug: 'fatec', label: 'Fatec', profile: 'vestibular' },
];

/** One seed row, with the API's own column defaults filled in. */
function profile(
  slug: string,
  label: string,
  description: string,
  overrides: Partial<Omit<Profile, 'slug' | 'label' | 'description' | 'features'>> & {
    features?: Partial<ProfileFeatures>;
  } = {},
): Profile {
  const { features, ...rest } = overrides;
  return {
    slug,
    label,
    description,
    assignableTo: 'user',
    groupLimit: 1,
    groupMode: 'owner_only',
    privateQuota: 50,
    dependentsLimit: 0,
    userPersonasLimit: 3,
    imageDailyLimit: 5,
    groupImageDailyLimit: 10,
    // A coluna é anulável; todo perfil semeado acima aponta a própria voz.
    defaultPersonaId: null,
    enabled: true,
    sortOrder: 0,
    userCount: 0,
    ...rest,
    features: { ...NO_FEATURES, ...features },
  };
}

/** A subarea as the tree seed spells it: the slug is data, never derived here. */
interface SeedSubarea {
  slug: string;
  label: string;
  description: string;
  /** Baseline corpus; the mock client shifts it by what the session adds. */
  documentCount?: number;
}

/**
 * The platform taxonomy as a two-level tree, partitioned by profile — the shape
 * migration 0046 builds.
 *
 * The sixteen slugs the platform already serves are RE-PARENTED here, never
 * renamed: a slug is what documents are filed by, so `sf-industrias` keeps its
 * name and simply becomes a subarea of Salesforce Industries. Only a LEAF holds
 * documents, and every area is born with a locked "Geral" (added by
 * {@link taxonomyArea}) so there is always somewhere to file.
 *
 * This is a representative slice of the seeded tree, not the whole of it: enough
 * of each profile for the console — pickers, cascade, admin panel — to be
 * exercised offline.
 */
export const seedTaxonomy: TaxonomyArea[] = inProfileOrder([
  taxonomyArea('padrao', 'interno', 'Interno', 'Material do time: catálogo, processos e treinamentos.', [
    {
      slug: 'interno-catalogo',
      label: 'Catálogo de produtos e ofertas',
      description: 'Catálogo EPC, ofertas, produtos, atributos e regras de elegibilidade.',
      documentCount: 2,
    },
    {
      slug: 'interno-processos',
      label: 'Processos e políticas do time',
      description: 'Políticas comerciais, processos internos e material de atendimento.',
      documentCount: 2,
    },
    {
      slug: 'treinamentos',
      label: 'Treinamentos e enriquecimentos',
      description: 'Pacotes de conhecimento derivados de treinamentos (resumos, Q&A e fatos).',
      documentCount: 6,
    },
  ]),
  taxonomyArea('padrao', 'diversos', 'Diversos', 'Assuntos do dia a dia que não pertencem a um domínio.', [
    {
      slug: 'diversos-curiosidades',
      label: 'Curiosidades e cultura geral',
      description: 'Curiosidades, história do cotidiano e cultura geral.',
    },
    {
      slug: 'diversos-produtividade',
      label: 'Produtividade e organização pessoal',
      description: 'Rotinas, métodos de estudo e organização pessoal.',
    },
  ]),
  taxonomyArea('tecnico', 'salesforce-core', 'Salesforce Core', 'A plataforma Salesforce e suas clouds padrão.', [
    {
      slug: 'sf-plataforma-apex-ui',
      label: 'Plataforma, Apex & UI',
      description: 'Apex, SOQL, limites de governador, Lightning Web Components, Aura e Visualforce.',
      documentCount: 154,
    },
    {
      slug: 'sf-apis-plataforma',
      label: 'APIs de Plataforma',
      description: 'REST, SOAP, Bulk 2.0, Streaming, Metadata, Tooling e Connect REST APIs.',
      documentCount: 87,
    },
    {
      slug: 'sf-crm-analytics',
      label: 'CRM Analytics',
      description: 'Datasets, dataflows, SAQL, dashboards e Einstein Discovery.',
      documentCount: 63,
    },
    {
      slug: 'sf-service-cloud',
      label: 'Service Cloud',
      description: 'Casos, Knowledge, Omni-Channel, Service Cloud Voice e Field Service.',
      documentCount: 51,
    },
    {
      slug: 'sf-dx-cli-pacotes',
      label: 'DX, CLI & Pacotes',
      description: 'sf CLI, scratch orgs, source tracking, pacotes gerenciados e AppExchange.',
      documentCount: 58,
    },
    {
      slug: 'sf-revenue-cpq',
      label: 'Revenue, CPQ & Contratos',
      description: 'Revenue Cloud, CPQ, cotações, precificação, billing e contratos.',
      documentCount: 72,
    },
    {
      slug: 'sf-mobile-sdks',
      label: 'Mobile & SDKs',
      description: 'Mobile SDK para iOS e Android, MobileSync, SmartStore e Mobile Publisher.',
      documentCount: 19,
    },
    {
      slug: 'sf-seguranca-identidade',
      label: 'Segurança & Identidade',
      description: 'OAuth, Connected Apps, SSO, perfis, permissões e compartilhamento.',
      documentCount: 45,
    },
    {
      slug: 'sf-experience-cloud',
      label: 'Experience Cloud',
      description: 'Sites, templates, componentes e compartilhamento em Experience Cloud.',
      documentCount: 24,
    },
    {
      slug: 'sf-usuario-admin',
      label: 'Usuário/Admin (Help)',
      description: 'Artigos de ajuda e configuração do dia a dia de administradores e usuários.',
      // Zero on purpose: the API LEFT JOINs the counts, so an area nobody has
      // filed anything under still appears — and the console renders that too.
      documentCount: 0,
    },
    {
      slug: 'sf-referencias',
      label: 'Referências & Boas Práticas',
      description: 'Guias de arquitetura, padrões de projeto e material de referência transversal.',
      documentCount: 33,
    },
    {
      slug: 'sf-flow-automacao',
      label: 'Flow e automação',
      description: 'Flow Builder, automações declarativas, orquestrações e approvals.',
    },
    {
      slug: 'sf-lwc',
      label: 'Lightning Web Components',
      description: 'LWC, wire adapters, eventos, testes Jest e base components.',
    },
  ]),
  taxonomyArea(
    'tecnico',
    'salesforce-industries-cme',
    'Salesforce Industries (CME)',
    'Communications, Media e Energy: EPC, CPQ, Order Management e OmniStudio.',
    [
      {
        slug: 'sf-industrias',
        label: 'Indústrias',
        description: 'Salesforce Industries (ex-Vlocity) e as clouds verticais, com seus modelos de dados.',
        documentCount: 96,
      },
      {
        slug: 'cme-epc-catalogo',
        label: 'EPC e catálogo',
        description: 'Enterprise Product Catalog: produtos, atributos, bundles e picklists.',
      },
      {
        slug: 'cme-cpq-carrinho',
        label: 'CPQ e carrinho',
        description: 'Order capture, carrinho, MACD e asset-based ordering.',
      },
      {
        slug: 'cme-omnistudio',
        label: 'OmniStudio',
        description: 'OmniScript, FlexCard, Integration Procedure e DataRaptor.',
      },
    ],
  ),
  taxonomyArea('tecnico', 'integracao-api', 'Integração e APIs', 'Contratos, mensageria e autenticação entre sistemas.', [
    {
      slug: 'tmf-open-apis',
      label: 'TM Forum — Open APIs e SID',
      description: 'Open APIs do TM Forum, modelo SID, eTOM e o Open Digital Architecture.',
      documentCount: 41,
    },
    {
      slug: 'int-rest-graphql',
      label: 'REST e GraphQL',
      description: 'Desenho de APIs REST, GraphQL, paginação, versionamento e erros.',
    },
    {
      slug: 'int-mensageria-eventos',
      label: 'Mensageria e eventos',
      description: 'Filas, tópicos, eventos de plataforma e entrega garantida.',
    },
  ]),
  taxonomyArea('concursos', 'conc-lingua-portuguesa', 'Língua Portuguesa', 'Gramática, interpretação e redação oficial para provas.', [
    {
      slug: 'conc-port-gramatica',
      label: 'Gramática',
      description: 'Classes de palavras, sintaxe, crase, pontuação e regência.',
    },
    {
      slug: 'conc-port-interpretacao-texto',
      label: 'Interpretação de texto',
      description: 'Compreensão, inferência, coesão, coerência e tipologia textual.',
    },
    {
      slug: 'conc-port-redacao-oficial',
      label: 'Redação oficial',
      description: 'Ofício, memorando, padrão ofício e o Manual da Presidência.',
    },
  ]),
  taxonomyArea('concursos', 'conc-direito', 'Direito', 'Os ramos do Direito cobrados nos editais.', [
    {
      slug: 'conc-dir-constitucional',
      label: 'Direito Constitucional',
      description: 'Direitos fundamentais, organização do Estado, controle de constitucionalidade.',
    },
    {
      slug: 'conc-dir-administrativo',
      label: 'Direito Administrativo',
      description: 'Atos administrativos, licitações, servidores, improbidade e responsabilidade.',
    },
    {
      slug: 'conc-dir-tributario',
      label: 'Direito Tributário',
      description: 'Competência, obrigação, crédito tributário, imunidades e isenções.',
    },
  ]),
  taxonomyArea(
    'concursos',
    'conc-raciocinio-logico-matematica',
    'Raciocínio Lógico e Matemática',
    'Lógica proposicional, matemática básica, financeira e estatística.',
    [
      {
        slug: 'conc-rlm-logica-proposicional',
        label: 'Lógica proposicional',
        description: 'Proposições, tabelas-verdade, equivalências e argumentos válidos.',
      },
      {
        slug: 'conc-rlm-matematica-financeira',
        label: 'Matemática financeira',
        description: 'Juros simples e compostos, descontos, séries e taxas equivalentes.',
      },
    ],
  ),
  taxonomyArea('concursos', 'conc-contabilidade', 'Contabilidade Geral e Avançada', 'Contabilidade societária, pública e de custos para provas.', [
    {
      slug: 'conc-cont-basica-intermediaria',
      label: 'Contabilidade básica e intermediária',
      description: 'Patrimônio, lançamentos, balancete e demonstrações contábeis.',
    },
    {
      slug: 'conc-cont-publica-casp',
      label: 'Contabilidade pública (CASP)',
      description: 'PCASP, MCASP, demonstrações do setor público e regimes contábeis.',
    },
  ]),
  taxonomyArea('concursos', 'conc-auditoria', 'Auditoria', 'Normas, auditoria governamental e controles internos.', [
    {
      slug: 'conc-aud-normas-nbc-ta',
      label: 'Normas de auditoria (NBC TA)',
      description: 'Planejamento, risco, evidência, papéis de trabalho e relatório do auditor.',
    },
    {
      slug: 'conc-aud-governamental',
      label: 'Auditoria governamental',
      description: 'Auditoria no setor público, achados, recomendações e controle externo.',
    },
  ]),
  taxonomyArea('concursos', 'conc-tecnologia-informacao', 'Tecnologia da Informação', 'TI cobrada em editais: dados, redes, segurança e governança.', [
    {
      slug: 'conc-ti-bancos-dados',
      label: 'Bancos de dados',
      description: 'Modelagem, SQL, normalização, transações e data warehouse.',
    },
    {
      slug: 'conc-ti-seguranca-informacao',
      label: 'Segurança da informação',
      description: 'Criptografia, controles de acesso, gestão de incidentes e normas ISO 27001.',
    },
  ]),
  taxonomyArea(
    'vestibular',
    'vest-linguagens',
    'Linguagens, Códigos e suas Tecnologias',
    'Português, literatura, línguas estrangeiras e artes no ENEM e nos vestibulares.',
    [
      {
        slug: 'vest-ling-portugues-gramatica',
        label: 'Português e gramática',
        description: 'Norma-padrão, variação linguística, funções da linguagem e figuras.',
      },
      {
        slug: 'vest-ling-literatura',
        label: 'Literatura',
        description: 'Escolas literárias, obras obrigatórias e análise de textos literários.',
      },
      {
        slug: 'vest-ling-interpretacao-texto',
        label: 'Interpretação de texto',
        description: 'Leitura de textos verbais e não verbais, charges, gráficos e infográficos.',
      },
    ],
  ),
  taxonomyArea(
    'vestibular',
    'vest-ciencias-humanas',
    'Ciências Humanas e suas Tecnologias',
    'História, geografia, filosofia e sociologia no ENEM e nos vestibulares.',
    [
      {
        slug: 'vest-hum-historia',
        label: 'História',
        description: 'História geral e do Brasil, processos políticos, econômicos e sociais.',
      },
      {
        slug: 'vest-hum-geografia',
        label: 'Geografia',
        description: 'Geografia física e humana, urbanização, população e geopolítica.',
      },
    ],
  ),
  taxonomyArea(
    'vestibular',
    'vest-ciencias-natureza',
    'Ciências da Natureza e suas Tecnologias',
    'Biologia, química e física com a leitura de dados que as provas cobram.',
    [
      {
        slug: 'vest-nat-biologia',
        label: 'Biologia',
        description: 'Citologia, genética, ecologia, evolução e fisiologia humana.',
      },
      {
        slug: 'vest-nat-quimica',
        label: 'Química',
        description: 'Estequiometria, soluções, termoquímica, eletroquímica e orgânica.',
      },
      {
        slug: 'vest-nat-fisica',
        label: 'Física',
        description: 'Mecânica, termologia, óptica, ondulatória e eletromagnetismo.',
      },
    ],
  ),
  taxonomyArea('vestibular', 'vest-matematica', 'Matemática e suas Tecnologias', 'Álgebra, geometria, estatística e problemas aplicados.', [
    {
      slug: 'vest-mat-algebra-funcoes',
      label: 'Álgebra e funções',
      description: 'Funções afim, quadrática, exponencial, logarítmica e progressões.',
    },
    {
      slug: 'vest-mat-geometria',
      label: 'Geometria',
      description: 'Geometria plana, espacial e analítica; áreas, volumes e trigonometria.',
    },
  ]),
  taxonomyArea('vestibular', 'vest-redacao', 'Redação', 'Dissertação argumentativa, repertório e as competências do ENEM.', [
    {
      slug: 'vest-red-dissertativo-argumentativo',
      label: 'Dissertativo-argumentativo',
      description: 'Estrutura, tese, argumentação e proposta de intervenção.',
    },
    {
      slug: 'vest-red-competencias-enem',
      label: 'Competências do ENEM',
      description: 'As cinco competências, critérios de correção e erros que zeram.',
    },
  ]),
]);

/**
 * Stamps each area's `sortOrder` with its position INSIDE its profile. The seeded
 * order is the one the taxonomy was designed in — "Interno" before "Diversos",
 * "Salesforce Core" before the rest — and `sort_order` exists precisely so the
 * picker does not fall back to the alphabet.
 */
function inProfileOrder(areas: TaxonomyArea[]): TaxonomyArea[] {
  const seen = new Map<string, number>();
  return areas.map((area) => {
    const next = seen.get(area.profile) ?? 0;
    seen.set(area.profile, next + 1);
    return { ...area, sortOrder: next };
  });
}

/**
 * One area plus the locked "Geral" every area is born with — first in the list,
 * as the API orders it, and the destination a cascade pre-selects.
 */
function taxonomyArea(
  profileSlug: string,
  slug: string,
  label: string,
  description: string,
  subareas: SeedSubarea[],
): TaxonomyArea {
  const children: TaxonomyNode[] = [
    node(`${slug}-geral`, 'Geral', `Conteúdo de ${label} que não pertence a uma subárea específica.`, 0, true),
    ...subareas.map((sub, index) => node(sub.slug, sub.label, sub.description, sub.documentCount ?? 0, false, index + 1)),
  ];
  return {
    id: `area-${slug}`,
    slug,
    label,
    description,
    profile: profileSlug,
    enabled: true,
    sortOrder: 0,
    documentCount: children.reduce((sum, child) => sum + child.documentCount, 0),
    subareas: children,
  };
}

function node(
  slug: string,
  label: string,
  description: string,
  documentCount: number,
  isGeneral: boolean,
  sortOrder = 0,
): TaxonomyNode {
  return { id: `node-${slug}`, slug, label, description, enabled: true, isGeneral, sortOrder, documentCount };
}

/**
 * The area vocabulary the knowledge endpoint serves, flattened from the tree
 * above so the two can never disagree. Order is the API's: by profile, then by
 * area, with each area immediately followed by its subareas — the Geral first.
 *
 * `family` keeps carrying one level of grouping for free, as it always did: the
 * profile for an area, the parent area for a subarea.
 */
export const seedAreas: Omit<KnowledgeArea, 'ownedByMe'>[] = seedTaxonomy.flatMap((area) => [
  {
    slug: area.slug,
    label: area.label,
    family: area.profile,
    description: area.description,
    documentCount: area.documentCount,
    profile: area.profile,
    kind: 'area' as const,
    parentSlug: null,
    isGeneral: false,
    sortOrder: area.sortOrder,
  },
  ...area.subareas.map((sub) => ({
    slug: sub.slug,
    label: sub.label,
    family: area.slug,
    description: sub.description,
    documentCount: sub.documentCount,
    profile: area.profile,
    kind: 'subarea' as const,
    parentSlug: area.slug,
    isGeneral: sub.isGeneral,
    sortOrder: sub.sortOrder,
  })),
]);

/**
 * The corpus this offline session can see: the shared global acervo plus one
 * document on its OWN shelf (`ownedByMe`). Both kinds are seeded on purpose —
 * a listing that held only one of them would never render the other badge, and
 * the "Meu" / "Global" distinction is the visible half of private shelves.
 *
 * A THIRD kind is seeded (`doc-0005`): a document on somebody else's shelf. It
 * is deliberately absent from every ordinary listing — that is the invariant
 * private shelves exist for — and appears only in the admin inventory
 * (`scope=all`), which is the only way to exercise the "Todos os donos" view
 * offline.
 *
 * **Two checksums are deliberately shared**, because the shelf transition's 409
 * is unreachable offline while every document holds unique bytes:
 *
 * - `d4e5f6` — `doc-0002` (acervo global) and `doc-0006` (minha base). Moving
 *   either one onto the other's shelf is the collision the partial UNIQUE
 *   raises, in BOTH directions;
 * - `g7h8i9` — `doc-0003` (minha base) and `doc-0005` (a base de OUTRA pessoa).
 *   Publishing `doc-0003` must still succeed: a third person's shelf is not the
 *   destination and has its own UNIQUE. It is the counter-example that keeps
 *   "the destination shelf" from being read as "anything that is not mine".
 */
export const seedDocuments: Document[] = [
  {
    // Uploaded and retained: the only shape that offers a download.
    id: 'doc-0001',
    visibility: 'global',
    source: 'Upload',
    title: 'Catálogo EPC — Conceitos e relacionamentos',
    uri: 'knowledge-docs/doc-0001/catalogo-epc.pdf',
    docType: 'spec',
    area: 'interno-catalogo',
    version: '2.1',
    ownerTeam: 'Time de Agentes',
    checksum: 'a1b2c3',
    ingestedAt: daysAgo(3),
    indexedAt: daysAgo(3),
    chunkCount: 84,
    downloadable: true,
    ownedByMe: false,
    ownerDisplayName: null,
    ownerUserId: null,
  },
  {
    id: 'doc-0002',
    visibility: 'global',
    source: 'Google Drive',
    title: 'Política comercial de ofertas convergentes',
    uri: 'https://drive.interno/politica-convergente',
    docType: 'policy',
    area: 'interno-processos',
    version: '5',
    ownerTeam: 'Comercial',
    checksum: 'd4e5f6',
    ingestedAt: daysAgo(7),
    indexedAt: daysAgo(1),
    chunkCount: 42,
    downloadable: false,
    ownedByMe: false,
    ownerDisplayName: null,
    ownerUserId: null,
  },
  {
    id: 'doc-0003',
    visibility: 'public',
    source: 'Upload',
    title: 'Glossário de telecom e regulação Anatel',
    uri: null,
    docType: 'glossary',
    area: 'interno-catalogo',
    version: '1',
    ownerTeam: 'Regulatório',
    checksum: 'g7h8i9',
    ingestedAt: daysAgo(14),
    indexedAt: daysAgo(14),
    chunkCount: 167,
    downloadable: false,
    ownedByMe: true,
    ownerDisplayName: null,
    ownerUserId: null,
  },
  {
    // My own copy of what the acervo already holds (doc-0002's bytes): the pair
    // that makes the 409 of a shelf move reachable without a backend.
    id: 'doc-0006',
    visibility: 'private',
    source: 'Upload',
    title: 'Tabela de preços — rascunho pessoal',
    uri: null,
    docType: 'other',
    area: null,
    version: null,
    ownerTeam: null,
    checksum: 'd4e5f6',
    ingestedAt: daysAgo(1),
    indexedAt: daysAgo(1),
    chunkCount: 9,
    downloadable: false,
    ownedByMe: true,
    ownerDisplayName: null,
    ownerUserId: null,
  },
  {
    // Ingested before retention existed: `uri` is the bare filename, so there is
    // no file to download.
    id: 'doc-0004',
    visibility: 'global',
    source: 'Upload',
    title: 'FAQ de atendimento — banda larga fibra',
    uri: 'faq-fibra.docx',
    docType: 'faq',
    area: 'interno-processos',
    version: '3',
    ownerTeam: 'Time de Agentes',
    checksum: 'j1k2l3',
    ingestedAt: null,
    indexedAt: null,
    chunkCount: 0,
    downloadable: false,
    ownedByMe: false,
    ownerDisplayName: null,
    ownerUserId: null,
  },
  {
    // Somebody ELSE's private shelf: listed only by the admin inventory. The
    // owner id is what the API's owner JOIN would serve; offline there is no
    // app_user row to point at (one would shift every users fixture), so the id
    // is synthetic and the mock maps it to the e-mail her areas key on.
    id: 'doc-0005',
    visibility: 'private',
    source: 'Upload',
    title: 'Notas pessoais de migração CPQ',
    uri: 'knowledge-docs/doc-0005/notas.pdf',
    docType: 'other',
    area: null,
    version: null,
    ownerTeam: null,
    // The same bytes as doc-0003, on a DIFFERENT private shelf — which the API
    // allows (the UNIQUE is per shelf) and which publishing doc-0003 must not
    // mistake for a collision.
    checksum: 'g7h8i9',
    ingestedAt: daysAgo(2),
    indexedAt: daysAgo(2),
    chunkCount: 12,
    downloadable: true,
    ownedByMe: false,
    ownerDisplayName: 'Marlena Ferreira',
    ownerUserId: 'usr-0006',
  },
];

/**
 * Older, uneventful runs so the history is longer than one page (25 rows).
 * A batch upload produces one run per file, so the pager is the normal case —
 * not an edge one — and the offline console has to exercise it.
 */
const settledHistory = (count: number): IngestionRun[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `run-hist-${String(i + 1).padStart(3, '0')}`,
    startedAt: daysAgo(3 + i),
    finishedAt: daysAgo(3 + i),
    status: 'succeeded' as const,
    docsProcessed: 1,
    chunksCreated: 40 + i,
    docsTotal: 1,
    chunksTotal: 40 + i,
    label: `documento-${i + 1}.pdf`,
    workStartedAt: daysAgo(3 + i),
    heartbeatAt: daysAgo(3 + i),
    error: null,
  }));

export const seedIngestionRuns: IngestionRun[] = [
  {
    id: 'run-0001',
    startedAt: hoursAgo(2),
    finishedAt: hoursAgo(2),
    status: 'succeeded',
    docsProcessed: 4,
    chunksCreated: 293,
    docsTotal: 4,
    chunksTotal: 293,
    label: null,
    workStartedAt: hoursAgo(2),
    heartbeatAt: hoursAgo(2),
    error: null,
  },
  {
    id: 'run-0002',
    startedAt: daysAgo(2),
    finishedAt: daysAgo(2),
    status: 'failed',
    docsProcessed: 1,
    chunksCreated: 0,
    docsTotal: 4,
    chunksTotal: 293,
    label: 'glossario-anatel.pdf',
    workStartedAt: daysAgo(2),
    heartbeatAt: daysAgo(2),
    error: 'Falha ao gerar embeddings: tempo limite excedido no provedor.',
  },
  {
    // Running with partial progress, so the progress bar has something real to
    // show offline.
    id: 'run-0003',
    startedAt: secondsAgo(180),
    finishedAt: null,
    status: 'running',
    docsProcessed: 2,
    chunksCreated: 51,
    docsTotal: 4,
    chunksTotal: 293,
    label: null,
    workStartedAt: secondsAgo(180),
    heartbeatAt: secondsAgo(5),
    error: null,
  },
  {
    // Accepted and still waiting at the ingestion gate: `workStartedAt` is null,
    // which is the ONLY thing that separates "Na fila" from "Processando".
    id: 'run-0004',
    startedAt: secondsAgo(20),
    finishedAt: null,
    status: 'running',
    docsProcessed: 0,
    chunksCreated: 0,
    docsTotal: 1,
    chunksTotal: 0,
    label: 'catalogo-epc.pdf',
    workStartedAt: null,
    heartbeatAt: secondsAgo(5),
    error: null,
  },
  ...settledHistory(24),
];

export const seedInteractions: QAInteraction[] = [
  {
    id: 'qa-0001',
    channel: 'whatsapp',
    userRef: '+55 11 9****-1234',
    question: 'Qual a diferença entre o plano Fibra 500 e o Fibra 1 Giga?',
    answer:
      'O Fibra 500 entrega 500 Mbps de download com Wi-Fi 6 incluso, indicado para uso residencial. O Fibra 1 Giga entrega 1 Gbps, oferece IP fixo opcional e prioridade no suporte técnico.',
    sources: [
      {
        documentId: 'doc-0001',
        documentTitle: 'Catálogo EPC — Conceitos e relacionamentos',
        chunkId: 'chunk-12',
        ordinal: 12,
        snippet: 'A oferta Fibra 1 Giga inclui prioridade de suporte e IP fixo opcional...',
      },
    ],
    confidence: 0.92,
    createdAt: hoursAgo(3),
  },
  {
    id: 'qa-0002',
    channel: 'web',
    userRef: 'ana.souza',
    question: 'O combo residencial já pode ser vendido?',
    answer:
      'Não. O Combo Residencial Fibra + Móvel está com status de rascunho no catálogo e ainda não foi homologado para venda.',
    sources: [
      {
        documentId: 'doc-0002',
        documentTitle: 'Política comercial de ofertas convergentes',
        chunkId: 'chunk-3',
        ordinal: 3,
        snippet: 'Ofertas em rascunho não devem ser ofertadas até homologação comercial...',
      },
    ],
    confidence: 0.78,
    createdAt: hoursAgo(20),
  },
  {
    id: 'qa-0003',
    channel: 'whatsapp',
    userRef: '+55 21 9****-5678',
    question: 'Qual a franquia de dados do plano controle?',
    answer:
      'O Plano Móvel Controle oferece 20GB de internet, com ligações ilimitadas e aplicativos de mensagens inclusos.',
    sources: [],
    confidence: 0.41,
    createdAt: daysAgo(1),
  },
];

/**
 * Which persona signed each seeded answer. Not a field of the interaction on the
 * wire (the history never exposes it), so the mock keeps it beside the rows, the
 * way the API keeps `qa_interaction.agent_id`. An absent entry is the asker's
 * official voice answering, exactly like a NULL `agent_id`.
 */
/** One recorded turn of a seeded thread, the way the API keeps it in `qa_interaction`. */
export interface SeededConversationTurn {
  interactionId: string;
  question: string;
  answer: string;
  at: string;
  /** Who asked, in a WhatsApp group thread. */
  senderName?: string;
  agentId?: string;
  sources: Citation[];
}

export interface SeededConversation {
  /**
   * A conversa como a API a guarda. A `preview` não é guardada: é a última
   * resposta cortada numa linha, então o mock a deriva dos `turns` a cada leitura
   * e ela não tem como divergir deles.
   */
  conversation: Omit<Conversation, 'preview'>;
  turns: SeededConversationTurn[];
  archived: boolean;
}

const catalogCitation: Citation = {
  documentId: 'doc-0001',
  documentTitle: 'Catálogo EPC — Conceitos e relacionamentos',
  chunkId: 'chunk-12',
  ordinal: 12,
  snippet: 'A oferta Fibra 1 Giga inclui prioridade de suporte e IP fixo opcional...',
  uri: 'knowledge-docs/doc-0001/catalogo-epc.pdf',
};

function seededThread(
  conversation: Omit<
    Conversation,
    'createdAt' | 'lastMessageAt' | 'readOnly' | 'pinnedAt' | 'preview'
  > & { pinnedAt?: string },
  turns: SeededConversationTurn[],
): SeededConversation {
  return {
    conversation: {
      ...conversation,
      createdAt: turns[0]!.at,
      lastMessageAt: turns.at(-1)!.at,
      readOnly: conversation.surface === 'whatsapp_group',
      pinnedAt: conversation.pinnedAt ?? null,
    },
    turns,
    archived: false,
  };
}

/**
 * The offline history: one thread per origin, spread over two months so the
 * side list groups by month and every tag shows up. The chatbot one is pinned
 * (above a more recent thread), and the oldest answer is long enough to be cut
 * in the preview.
 */
export const seedConversations: SeededConversation[] = [
  seededThread(
    { id: 'conv-0001', surface: 'knowledge', title: 'Diferença entre Fibra 500 e Fibra 1 Giga', externalRef: null },
    [
      {
        interactionId: 'qa-conv-0001-1',
        question: 'Qual a diferença entre o Fibra 500 e o Fibra 1 Giga?',
        answer:
          'O Fibra 500 entrega 500 Mbps com Wi-Fi 6 incluso. O Fibra 1 Giga entrega 1 Gbps, IP fixo opcional e prioridade no suporte.',
        at: hoursAgo(2),
        agentId: 'arquiteto-sf-communications',
        sources: [catalogCitation],
      },
      {
        interactionId: 'qa-conv-0001-2',
        question: 'E qual deles tem fidelidade?',
        answer: 'Os dois têm fidelidade de 12 meses; o Fibra 1 Giga dispensa a multa após o sexto mês.',
        at: hoursAgo(1.5),
        sources: [catalogCitation],
      },
    ],
  ),
  seededThread(
    {
      id: 'conv-0002',
      surface: 'chatbot',
      title: 'Como cadastrar um documento na minha base',
      externalRef: null,
      pinnedAt: hoursAgo(12),
    },
    [
      {
        interactionId: 'qa-conv-0002-1',
        question: 'Como cadastrar um documento na minha base?',
        answer: 'Em Conhecimento › Documentos, use "Enviar documento", escolha a área e confirme.',
        at: daysAgo(1),
        sources: [],
      },
    ],
  ),
  seededThread(
    {
      id: 'conv-0003',
      surface: 'whatsapp_direct',
      title: 'Conversa no WhatsApp',
      externalRef: '+55 11 9****-1234',
    },
    [
      {
        interactionId: 'qa-conv-0003-1',
        question: 'O combo residencial já pode ser vendido?',
        answer: 'Ainda não: o Combo Residencial está em rascunho e não foi homologado para venda.',
        at: daysAgo(3),
        sources: [],
      },
    ],
  ),
  seededThread(
    { id: 'conv-0004', surface: 'whatsapp_group', title: 'Equipe Comercial Sul', externalRef: 'grp-mock-0001' },
    [
      {
        interactionId: 'qa-conv-0004-1',
        question: '@shiba quais ofertas de banda larga estão ativas?',
        answer: 'Estão ativas o Fibra 300, o Fibra 500 e o Fibra 1 Giga.',
        at: daysAgo(9),
        senderName: 'Bruna Lima',
        sources: [catalogCitation],
      },
      {
        interactionId: 'qa-conv-0004-2',
        question: '@shiba e o prazo de instalação?',
        answer: 'A instalação acontece em até 5 dias úteis após a aprovação do pedido.',
        at: daysAgo(9),
        senderName: 'Rafael Costa',
        sources: [],
      },
    ],
  ),
  seededThread(
    { id: 'conv-0005', surface: 'knowledge', title: 'Regras de elegibilidade por região', externalRef: null },
    [
      {
        interactionId: 'qa-conv-0005-1',
        question: 'Quais são as regras de elegibilidade por região?',
        answer:
          'Cada oferta define elegibilidade por região, perfil do cliente e disponibilidade técnica. ' +
          'A consulta é feita pelo endereço do cliente: se o CEP está coberto pela rede, a oferta aparece ' +
          'como elegível; senão, o catálogo sugere a alternativa mais próxima.',
        at: daysAgo(40),
        sources: [catalogCitation],
      },
    ],
  ),
];

export const seedInteractionAgents: Record<string, string> = {
  'qa-0001': 'arquiteto-sf-communications',
  'qa-0003': 'arquiteto-sf-communications',
};

export const seedDecks: Deck[] = [
  {
    id: 'deck-0001',
    requestedBy: 'ana.souza',
    title: 'Apresentação — ofertas convergentes 2026',
    sourceText: 'Resumo da estratégia de ofertas convergentes para o trimestre...',
    status: 'ready',
    artifactUri: 'https://artifacts.interno/decks/deck-0001.pptx',
    colorPrimary: null,
    colorSecondary: null,
    error: null,
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  {
    id: 'deck-0002',
    requestedBy: 'carlos.mendes',
    title: 'Treinamento de habilitação — plano controle',
    sourceText: 'Material de treinamento para o time sobre o plano controle...',
    status: 'rendering',
    artifactUri: null,
    colorPrimary: null,
    colorSecondary: null,
    error: null,
    createdAt: hoursAgo(1),
    updatedAt: hoursAgo(1),
  },
];

export const seedTrainings: Training[] = [
  {
    id: 'trn-0001',
    theme: 'Ofertas convergentes 2026',
    documentId: 'doc-0002',
    status: 'ready',
    model: 'glm-5.2',
    rawChunks: 128,
    summaryChunks: 16,
    qaPairs: 34,
    factChunks: 52,
    error: null,
    createdAt: daysAgo(2),
  },
  {
    id: 'trn-0002',
    theme: 'Regras Anatel de banda larga',
    documentId: null,
    status: 'failed',
    model: 'glm-5.2',
    rawChunks: 0,
    summaryChunks: 0,
    qaPairs: 0,
    factChunks: 0,
    error: 'Falha ao extrair texto do arquivo: PDF protegido por senha.',
    createdAt: daysAgo(1),
  },
  {
    // Recently created so the mock shows a live requested→processing→ready cycle
    // on first load (see MockApiClient.projectTraining).
    id: 'trn-0003',
    theme: 'Plano Controle — treinamento do time',
    documentId: null,
    status: 'processing',
    model: 'glm-5.2',
    rawChunks: 0,
    summaryChunks: 0,
    qaPairs: 0,
    factChunks: 0,
    error: null,
    createdAt: secondsAgo(2),
  },
];

export const seedUsers: AppUser[] = [
  {
    id: 'usr-0001',
    email: 'ana.souza@operadora.com.br',
    displayName: 'Ana Souza',
    role: 'admin',
    status: 'active',
    profileSlug: 'padrao',
    createdAt: daysAgo(300),
    lastLoginAt: hoursAgo(2),
    invitePending: false,
  },
  {
    id: 'usr-0002',
    email: 'carlos.mendes@operadora.com.br',
    displayName: 'Carlos Mendes',
    role: 'editor',
    status: 'active',
    profileSlug: 'business',
    createdAt: daysAgo(210),
    lastLoginAt: hoursAgo(8),
    invitePending: false,
  },
  {
    id: 'usr-0003',
    email: 'bruna.lima@operadora.com.br',
    displayName: 'Bruna Lima',
    role: 'member',
    status: 'active',
    profileSlug: 'padrao',
    createdAt: daysAgo(60),
    lastLoginAt: daysAgo(1),
    invitePending: false,
  },
  {
    id: 'usr-0004',
    email: 'antigo.colaborador@operadora.com.br',
    displayName: 'Antigo Colaborador',
    role: 'member',
    status: 'disabled',
    profileSlug: 'padrao',
    createdAt: daysAgo(2),
    lastLoginAt: null,
    invitePending: false,
  },
  {
    // Convidada e ainda sem primeiro acesso: a única linha em que o console
    // oferece "Reenviar convite".
    id: 'usr-0005',
    email: 'paula.ramos@operadora.com.br',
    displayName: 'Paula Ramos',
    role: 'member',
    status: 'active',
    profileSlug: 'padrao',
    createdAt: daysAgo(1),
    lastLoginAt: null,
    invitePending: true,
  },
];

/**
 * The admin's overview. A non-admin's is computed by the mock from the session's
 * own rows instead — it describes one shelf, not a seeded platform. The month
 * figures agree with {@link seedMetricsCost}, as the API's two reads do.
 */
export const seedPlatformOverview: PlatformOverview = {
  scope: 'platform',
  qaInteractions24h: 318,
  avgConfidence: 0.84,
  avgLatencyMs: 1840,
  qualityWindowDays: 30,
  documentsIndexed: 147,
  activeUsers: 12,
  lastIngestionAt: hoursAgo(2),
  cost: {
    currentMonthUsd: 142.37,
    projectedMonthUsd: 268.9,
    budgetUsd: 400,
    budgetUsedRatio: 142.37 / 400,
    projectedOverBudget: false,
    tokensIn: 1_284_600,
    tokensOut: 456_700,
  },
};

export const seedMetricsCost: MetricsCost = {
  currentMonthUsd: 142.37,
  projectedMonthUsd: 268.9,
  budgetUsd: 400,
  series: Array.from({ length: 14 }, (_, i) => {
    const base = 6 + Math.sin(i / 2) * 3 + i * 0.4;
    return {
      date: dayDate(13 - i),
      amountUsd: Number(base.toFixed(2)),
      tokensIn: Math.round(base * 9000),
      tokensOut: Math.round(base * 3200),
    };
  }),
  // As duas aberturas fecham com o mês: US$ 142,37 e 1.741.300 tokens
  // (1.284.600 de entrada + 456.700 de saída, os do overview), em 23.170
  // chamadas — como a API, que as lê da mesma tabela. Do mais caro ao mais barato.
  byModel: [
    { label: 'deepseek-flash', calls: 8_460, tokens: 884_600, usd: 49.2 },
    { label: 'kimi-k3', calls: 2_140, tokens: 351_900, usd: 40.05 },
    { label: 'gemini-3.8-flash', calls: 2_290, tokens: 289_700, usd: 27.1 },
    { label: 'glm-5.2', calls: 410, tokens: 118_700, usd: 17.62 },
    { label: 'gemini-embedding-2', calls: 9_870, tokens: 96_400, usd: 8.4 },
  ],
  // `outros` é o que a API não conseguiu atribuir a uma funcionalidade (feature NULL).
  byFeature: [
    { label: 'ask', calls: 7_480, tokens: 1_021_900, usd: 71.9 },
    { label: 'deck', calls: 640, tokens: 312_600, usd: 31.2 },
    { label: 'vision', calls: 1_050, tokens: 150_300, usd: 18.35 },
    { label: 'embeddings', calls: 9_870, tokens: 96_400, usd: 8.4 },
    { label: 'classifier', calls: 3_920, tokens: 98_800, usd: 7.52 },
    { label: 'outros', calls: 210, tokens: 61_300, usd: 5 },
  ],
};

/**
 * O vínculo de WhatsApp da operação simulada começa DESVINCULADO, porque é o
 * estado em que todo mundo chega e o único a partir do qual o fluxo inteiro
 * (pedir código → confirmar → desvincular) pode ser percorrido offline.
 */
export const seedWhatsAppLink: WhatsAppLink = {
  linked: false,
  phoneMasked: null,
  verifiedAt: null,
};

/**
 * A "DM" da operação simulada. No servidor real o código nasce do CSPRNG e sai
 * pelo agente; offline não existe telefone para receber nada, então o mock usa
 * um valor fixo — e o anuncia no console do navegador, que é o mais próximo de
 * uma DM que uma demo sem backend tem. Ele é constante justamente para que o
 * fluxo completo seja percorrível numa demo e por um teste e2e.
 */
export const MOCK_WHATSAPP_CODE = '123456';

/**
 * Um número que, na operação simulada, JÁ pertence a outra conta: é o que torna
 * o 409 genérico ("número indisponível para vínculo") alcançável offline, com
 * exatamente a mesma mensagem do servidor e sem dizer de quem ele é.
 */
export const MOCK_WHATSAPP_TAKEN_PHONE = '5511988887777';

/**
 * Um grupo da operação simulada como o BANCO o guarda: a escolha crua de ids
 * (`wa_group_agent`), e não a projeção do fio. As tags e a contagem de
 * indisponíveis saem dela na leitura, contra o elenco de quem está na cadeira,
 * exatamente como o servidor faz.
 */
export interface SeededWhatsAppGroup
  extends Pick<WhatsAppGroup, 'groupId' | 'status' | 'registeredAt' | 'subject' | 'agentsMode'> {
  agentIds: string[];
}

/**
 * Um grupo de exemplo, com nome e agentes escolhidos um a um, para que o cartão
 * (nome, tags, aviso de agente que sumiu, "Editar agentes") apareça numa demo
 * sem passar pelo código. Nasce SUSPENSO porque o vínculo nasce desfeito: é o
 * estado que o servidor deixa depois de um desvínculo, e vincular o reativa.
 *
 * A Marlena está na escolha de propósito: ela é a voz do Técnico, fora do
 * elenco do Padrão, então aparece como "1 agente não está mais disponível"
 * para quem está no Padrão. No limite de 1 grupo do Padrão, o botão de gerar
 * código começa desabilitado com o motivo, até o grupo ser removido.
 */
export const seedWhatsAppGroups: SeededWhatsAppGroup[] = [
  {
    groupId: '120363099887766554@g.us',
    status: 'suspended',
    registeredAt: '2026-09-02T10:00:00Z',
    subject: 'Família Souza',
    agentsMode: 'selected',
    agentIds: ['shiba', 'analista-funcional'],
  },
];

/**
 * O código de registro fixo da operação simulada — o par do MOCK_WHATSAPP_CODE.
 * No servidor ele nasce do CSPRNG sobre um alfabeto sem I/L/O/U; offline ele é
 * constante para que a demo tenha um valor previsível para "postar no grupo".
 */
export const MOCK_WHATSAPP_GROUP_CODE = 'HAB-K7M4P';

/**
 * O grupo que a demo registra quando alguém "posta" o código. Offline não há
 * WhatsApp onde postar nada, então o mock trata a geração do código como o ato
 * completo — é a única forma de a lista, o limite e a remoção ficarem
 * exercitáveis sem backend.
 */
export const MOCK_WHATSAPP_GROUP_ID = '12036304242424242@g.us';

/**
 * O código de vínculo de dependente da operação simulada — o par do
 * MOCK_WHATSAPP_GROUP_CODE, no alfabeto `FAM-[A-Z2-7]{5}` que o servidor usa.
 * Fixo pelo mesmo motivo dos outros dois: sem um valor previsível a demo e o
 * teste e2e não têm o que "mandar ao agente".
 */
export const MOCK_DEPENDENT_CODE = 'FAM-R3T6X';

/**
 * O número que a demo dá ao dependente quando ela simula o vínculo. Ele só
 * aparece MASCARADO em qualquer resposta: o console nunca recebe o número de um
 * dependente, que é quase sempre o de uma criança.
 */
export const MOCK_DEPENDENT_PHONE_JID = '5511955554444@s.whatsapp.net';

/**
 * O catálogo público de planos da operação simulada, espelhando a seed da
 * migration 0062 com o preço do Pro da 0066 (só as linhas `active` — a
 * Cortesia nunca é listada, senão o `/v1/billing/plans` a venderia de
 * graça). As capabilities vêm ORDENADAS como
 * o wire as manda (`BillingPlanResponse.from_domain` aplica `sorted()`).
 */
export const seedBillingPlans: BillingPlan[] = [
  {
    code: 'free',
    name: 'Free',
    priceCentsMonth: 0,
    priceCentsYear: null,
    introPriceCentsMonth: null,
    introMonths: null,
    trialDays: 0,
    tokenLimitMonth: 100_000,
    capabilities: ['chat', 'global_rag'],
  },
  {
    code: 'pro',
    name: 'Pro',
    priceCentsMonth: 1990,
    priceCentsYear: 19104,
    introPriceCentsMonth: 1592,
    introMonths: 6,
    trialDays: 7,
    tokenLimitMonth: 100_000_000,
    capabilities: [
      'agents',
      'chat',
      'decks',
      'global_rag',
      'private_rag',
      'reminders',
      'whatsapp',
    ],
  },
];

/**
 * A assinatura do cenário FREE da operação simulada: o ponto de partida do
 * fluxo comercial (checkout → pendente/trial → cancelamento), alcançado pelo
 * `MockApiClient({ plan: 'free' })` nos testes ou pela chave
 * {@link MOCK_PLAN_SCENARIO_KEY} no navegador (demo offline e suíte e2e).
 */
export const seedBillingSubscription: BillingSubscription = {
  planCode: 'free',
  planName: 'Free',
  status: 'free',
  cycle: null,
  trialEndsAt: null,
  currentPeriodEnd: null,
  introEndsAt: null,
  cancelAtPeriodEnd: false,
  cardBrand: null,
  cardLast4: null,
  pix: null,
};

/**
 * A assinatura do cenário PADRÃO: uma conta Pro ativa. É ela — e não a free —
 * que nasce no mock, porque o padrão existe para o console inteiro ser
 * alcançável offline (o getMe disse Pro desde sempre; WS9 reconciliou: agora a
 * visão comercial conta a MESMA história que o /me, nos dois sentidos — ver o
 * `currentEntitlement` do mock-client).
 */
export const seedBillingSubscriptionPro: BillingSubscription = {
  planCode: 'pro',
  planName: 'Pro',
  status: 'active',
  cycle: 'MONTHLY',
  trialEndsAt: null,
  currentPeriodEnd: null,
  introEndsAt: null,
  cancelAtPeriodEnd: false,
  cardBrand: 'visa',
  cardLast4: '4242',
  pix: null,
};

/**
 * A chave de localStorage que escolhe o cenário do plano na demo offline e na
 * suíte e2e (`free` liga o cenário gratuito; ausente ou qualquer outro valor é
 * o Pro padrão). Lida UMA vez, na construção do cliente — a operação simulada
 * é in-memory, então trocar de cenário pede reload.
 */
export const MOCK_PLAN_SCENARIO_KEY = 'habilita.mock-plan';

/**
 * A chave de localStorage que FECHA a contratação online na demo offline e na
 * suíte e2e (`off`; ausente ou qualquer outro valor é o checkout aberto, o
 * padrão): o catálogo passa a responder `checkoutEnabled: false`, como o
 * servidor sem gateway configurado. Lida como {@link MOCK_PLAN_SCENARIO_KEY}:
 * uma vez, na construção do cliente.
 */
export const MOCK_CHECKOUT_SCENARIO_KEY = 'habilita.mock-checkout';

/**
 * O código de indicação da conta da operação simulada — 8 caracteres no
 * alfabeto Crockford base32 do servidor (sem I/L/O/U). Fixo como os outros
 * códigos mock: sem um valor previsível a demo e o e2e não têm o que copiar.
 */
export const MOCK_REFERRAL_CODE = 'SH1BA202';

/**
 * O código de um INDICADOR que existe na operação simulada: o único que o
 * `attach` aceita, para o 404 ("código não encontrado") ser alcançável offline
 * com a mesma mensagem do servidor.
 */
export const MOCK_KNOWN_REFERRAL_CODE = 'C0NV1DAD';

/** O painel "minhas indicações" de uma conta nova: código próprio e zeros. */
export const seedBillingReferrals: BillingReferrals = {
  code: MOCK_REFERRAL_CODE,
  invited: 0,
  converted: 0,
  availablePercent: 0,
  credits: [],
};

/**
 * Modelos de IA da operação simulada. O catálogo espelha o backend
 * (model_catalog.py), e os padrões espelham o model_map.py: o DeepSeek V4.1
 * Flash em chat, decks, enriquecimento e visão, o Gemini na transcrição e na
 * geração de imagens, temperatura padrão 0.1. O GLM aparece INDISPONÍVEL (sem
 * chave de API na "operação" mock), para que o estado desabilitado do seletor
 * seja visível na demo offline. O chat começa com um override para o Kimi,
 * mostrando o distintivo "personalizado" e, de quebra, o controle de temperatura
 * DESABILITADO, porque o Kimi de raciocínio fixa a temperatura no transporte.
 * O deck guarda uma temperatura explícita para o caso oposto (controle ativo,
 * valor preenchido) também aparecer sem ninguém mexer em nada.
 */
export const seedAiModelSettings: AiModelSettings = {
  tasks: [
    {
      task: 'chat',
      capability: 'chat',
      defaultModel: 'deepseek-flash',
      override: 'kimi-k2.6',
      effectiveModel: 'kimi-k2.6',
      source: 'override',
      temperature: null,
      defaultTemperature: 0.1,
      supportsTemperature: false,
    },
    {
      task: 'deck',
      capability: 'chat',
      defaultModel: 'deepseek-flash',
      override: null,
      effectiveModel: 'deepseek-flash',
      source: 'pin',
      temperature: 0.7,
      defaultTemperature: 0.1,
      supportsTemperature: true,
    },
    {
      task: 'enrichment',
      capability: 'chat',
      defaultModel: 'deepseek-flash',
      override: null,
      effectiveModel: 'deepseek-flash',
      source: 'pin',
      temperature: null,
      defaultTemperature: 0.1,
      supportsTemperature: true,
    },
    {
      task: 'vision',
      capability: 'vision',
      defaultModel: 'deepseek-flash',
      override: null,
      effectiveModel: 'deepseek-flash',
      source: 'pin',
      temperature: null,
      defaultTemperature: 0,
      supportsTemperature: true,
    },
    {
      task: 'stt',
      capability: 'stt',
      defaultModel: 'gemini-3.5-flash-lite',
      override: null,
      effectiveModel: 'gemini-3.5-flash-lite',
      source: 'pin',
      temperature: null,
      defaultTemperature: 0.1,
      supportsTemperature: false,
    },
    {
      task: 'image',
      capability: 'image',
      defaultModel: 'gemini-3.1-flash-image',
      override: null,
      effectiveModel: 'gemini-3.1-flash-image',
      source: 'pin',
      temperature: null,
      defaultTemperature: 0.1,
      supportsTemperature: false,
    },
  ],
  models: [
    // Disponibilidade é por PROVEDOR: sem chave da Zhipu na operação mock, todo
    // GLM aparece indisponível. Os aliases da DeepSeek V4 não estão aqui: o
    // servidor ainda os reconhece num override gravado, mas não os oferece. Os
    // Kimi leem imagem (documentação da Moonshot); GLM e V4 Pro, só texto.
    { id: 'glm-4.6', label: 'GLM-4.6 (Zhipu)', provider: 'glm', available: false, capabilities: ['chat'], supportsTemperature: true },
    { id: 'glm-4.5-air', label: 'GLM-4.5 Air (Zhipu)', provider: 'glm', available: false, capabilities: ['chat'], supportsTemperature: true },
    { id: 'glm-5', label: 'GLM-5 (Zhipu)', provider: 'glm', available: false, capabilities: ['chat'], supportsTemperature: true },
    { id: 'glm-5.1', label: 'GLM-5.1 (Zhipu)', provider: 'glm', available: false, capabilities: ['chat'], supportsTemperature: true },
    { id: 'glm-5.2', label: 'GLM-5.2 (Zhipu, reasoning)', provider: 'glm', available: false, capabilities: ['chat'], supportsTemperature: true },
    { id: 'kimi-k2.6', label: 'Kimi K2.6 (Moonshot)', provider: 'kimi', available: true, capabilities: ['chat', 'vision'], supportsTemperature: false },
    { id: 'kimi-k3', label: 'Kimi K3 (Moonshot, reasoning)', provider: 'kimi', available: true, capabilities: ['chat', 'vision'], supportsTemperature: false },
    { id: 'deepseek-flash', label: 'DeepSeek V4.1 Flash', provider: 'deepseek', available: true, capabilities: ['chat', 'vision'], supportsTemperature: true },
    { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro', provider: 'deepseek', available: true, capabilities: ['chat'], supportsTemperature: true },
    {
      id: 'gemini-3.5-flash-lite',
      label: 'Gemini 3.5 Flash Lite (Google)',
      provider: 'google',
      available: true,
      capabilities: ['vision', 'stt'],
      supportsTemperature: true,
    },
    {
      id: 'gemini-3.5-flash',
      label: 'Gemini 3.5 Flash (Google)',
      provider: 'google',
      available: true,
      capabilities: ['vision', 'stt'],
      supportsTemperature: true,
    },
    {
      id: 'gemini-3.1-flash-image',
      label: 'Gemini 3.1 Flash Image (Google)',
      provider: 'google',
      available: true,
      capabilities: ['image'],
      supportsTemperature: false,
    },
    {
      id: 'gemini-3.1-flash-lite-image',
      label: 'Gemini 3.1 Flash Lite Image (Google)',
      provider: 'google',
      available: true,
      capabilities: ['image'],
      supportsTemperature: false,
    },
    {
      id: 'gemini-3-pro-image',
      label: 'Gemini 3 Pro Image (Google)',
      provider: 'google',
      available: true,
      capabilities: ['image'],
      supportsTemperature: false,
    },
  ],
};

export const SKILL_CARDS: SkillCard[] = [
  {
    id: 'qa',
    name: 'Perguntas e respostas',
    description: 'Responde sobre o catálogo e documentos indexados.',
  },
  {
    id: 'deck',
    name: 'Geração de apresentações',
    description: 'Gera roteiros de slides (.pptx) a partir de um tema ou texto.',
  },
  {
    id: 'rooms',
    name: 'Consulta de salas',
    description: 'Consulta disponibilidade de salas de reunião.',
  },
  {
    id: 'group-reports',
    name: 'Resumos de grupo',
    description: 'Resume conversas de grupos do WhatsApp em horários programados.',
  },
];

/**
 * The unified persona catalog, mirroring the rows migrations 0047–0057 seed: the
 * five Agentes padrão (∫hibΔ, Marlena, the two Fundamental teachers and the
 * Cuidadora), the three Salesforce architects, and the twelve teachers of
 * Vestibular and Concursos. There is no seed/override pair
 * any more — one row IS the persona — which is why every field below is simply
 * the persona's value.
 *
 * The prompts are one sentence each on purpose: the mock exists so the console
 * can be exercised offline, and the eight-line versions the migration carries
 * would bury the fields that actually drive this UI (profile, scope, tags).
 *
 * `linkedDocumentCount` starts at zero for everybody and is recomputed by the
 * mock client from its junction — a count seeded by hand would be the first
 * thing to disagree with the picker.
 */
export const seedAgents: AgentPersona[] = [
  // Agente padrão, voz oficial dos perfis de adulto (a 0057 a renomeou; o id e o
  // @shiba ficam). Enquanto for a voz de algum perfil, não pode ser desabilitada.
  seedPersona(
    'shiba',
    '∫hibΔ',
    'Assistente da plataforma',
    'Assistente geral da plataforma: responde qualquer assunto com base no conhecimento de quem pergunta, ajuda a estudar, trabalhar e organizar o dia a dia, e gera apresentações.',
    'Você é a Shiba, a assistente da plataforma. Responda em português do Brasil, de forma clara, direta e cordial, e diga com honestidade quando não souber.',
    {
      conversationStyle:
        'Frases curtas e tom cordial. Quando a mensagem for só conversa, responda em uma ou duas frases e ofereça ajuda.',
      handles: ['@shiba'],
      skills: ['qa', 'deck'],
      kind: 'default',
    },
  ),
  seedPersona(
    'analista-funcional',
    'Marlena',
    'Analista Funcional Sênior',
    'Responde perguntas gerais sobre o catálogo e gera apresentações e resumos.',
    'Você é Marlena, uma analista funcional sênior especializada no catálogo de produtos e ofertas da operadora. Responda com clareza, citando as fontes do catálogo quando disponíveis.',
    {
      conversationStyle:
        'Em conversa de corredor, responda em uma ou duas frases com bom humor e devolva o foco ao trabalho com uma pergunta leve.',
      // The only persona carrying the `group-reports` ficha, so the only one
      // whose daily-report prompt the console offers for editing.
      reportPrompt:
        'Resuma a conversa do grupo em *Principais atividades*, *Principais temas* e *TO DOs* por pessoa. Seja factual e não invente responsáveis nem prazos.',
      skills: ['qa', 'deck', 'rooms', 'group-reports'],
      kind: 'default',
    },
  ),
  seedPersona(
    'arquiteto-tmforum',
    'Arquiteta Especialista (TM Forum)',
    'Arquiteta de Sistemas TM Forum',
    'Especialista nos padrões TM Forum, SID e APIs TMF.',
    'Você é uma arquiteta de sistemas especialista em TM Forum. Responda focado em padrões SID, APIs TMF620/TMF622/etc., e modelagem de catálogo de produtos em telecom.',
    {
      handles: ['@tmforum', '@tmf'],
      profile: 'tecnico',
      tags: ['salesforce'],
      // `all` with areas is the BOOST the old `preferred_areas` was: it ranks,
      // it never hides. Restricting is what the other two modes are for.
      scopeAreas: ['tmf-open-apis'],
    },
  ),
  seedPersona(
    'arquiteto-sf-communications',
    'Arquiteta Salesforce CME',
    'Arquiteta de Sistemas Salesforce Communications Cloud',
    'Especialista em CPQ, EPC e Communications Cloud.',
    'Você é uma arquiteta de sistemas especialista em Salesforce Communications Cloud (CPQ/EPC). Responda focado em Vlocity/EPC, CPQ, decomposição de pedidos e modelagem de ofertas.',
    {
      handles: ['@cpq', '@epc', '@communications'],
      profile: 'tecnico',
      tags: ['salesforce'],
      scopeAreas: ['sf-industrias', 'cme-epc-catalogo', 'cme-cpq-carrinho'],
    },
  ),
  seedPersona(
    'arquiteto-sf-core',
    'Arquiteta Salesforce Core',
    'Arquiteta de Sistemas Salesforce Core',
    'Especialista em Salesforce Core: Sales, Service e arquitetura de dados.',
    'Você é uma arquiteta de sistemas especialista em Salesforce Core. Responda focado em dados, automações, Sales Cloud e Service Cloud.',
    {
      handles: ['@sfcore', '@core'],
      profile: 'tecnico',
      tags: ['salesforce'],
      scopeAreas: ['sf-plataforma-apex-ui', 'sf-service-cloud'],
    },
  ),
  teacher(
    'vest-linguagens',
    'Professora de Linguagens',
    'vestibular',
    'Professora de Linguagens, Códigos e Redação',
    'Leitura, literatura, gramática de uso e a redação do ENEM e dos vestibulares.',
    ['@linguagens'],
    ['enem', 'vestibular'],
    [
      'vest-ling-portugues-gramatica',
      'vest-ling-literatura',
      'vest-ling-interpretacao-texto',
      'vest-red-dissertativo-argumentativo',
      'vest-red-competencias-enem',
    ],
  ),
  teacher(
    'vest-humanas',
    'Professor de Ciências Humanas',
    'vestibular',
    'Professor de Ciências Humanas e suas Tecnologias',
    'História, geografia e o repertório que as provas cobram em texto-base.',
    ['@humanas'],
    ['enem'],
    ['vest-hum-historia', 'vest-hum-geografia'],
  ),
  teacher(
    'vest-natureza',
    'Professora de Ciências da Natureza',
    'vestibular',
    'Professora de Ciências da Natureza e suas Tecnologias',
    'Biologia, química e física com a leitura de gráficos e dados das provas.',
    ['@natureza'],
    ['enem'],
    ['vest-nat-biologia', 'vest-nat-quimica', 'vest-nat-fisica'],
  ),
  teacher(
    'vest-matematica',
    'Professor de Matemática',
    'vestibular',
    'Professor de Matemática e suas Tecnologias',
    'Álgebra, geometria e problemas aplicados, resolvidos passo a passo.',
    ['@matematica'],
    ['enem'],
    ['vest-mat-algebra-funcoes', 'vest-mat-geometria'],
  ),
  teacher(
    'conc-portugues',
    'Professora de Língua Portuguesa',
    'concursos',
    'Professora de Língua Portuguesa para concursos',
    'Gramática, interpretação e redação oficial no recorte das bancas.',
    ['@portugues'],
    ['concursos'],
    ['conc-port-gramatica', 'conc-port-interpretacao-texto', 'conc-port-redacao-oficial'],
  ),
  teacher(
    'conc-constitucional',
    'Professor de Direito Constitucional',
    'concursos',
    'Professor de Direito Constitucional',
    'Direitos fundamentais, organização do Estado e controle de constitucionalidade.',
    ['@constitucional'],
    ['concursos'],
    ['conc-dir-constitucional'],
  ),
  teacher(
    'conc-administrativo',
    'Professora de Direito Administrativo',
    'concursos',
    'Professora de Direito Administrativo',
    'Atos, agentes, licitações e responsabilidade da Administração.',
    ['@administrativo'],
    ['concursos'],
    ['conc-dir-administrativo'],
  ),
  teacher(
    'conc-tributario',
    'Professor de Direito Tributário',
    'concursos',
    'Professor de Direito Tributário',
    'Competência, obrigação, crédito tributário e a legislação de cada ente.',
    ['@tributario'],
    ['concursos', 'trilha-auditor-fiscal'],
    ['conc-dir-tributario'],
  ),
  teacher(
    'conc-contabilidade',
    'Professora de Contabilidade',
    'concursos',
    'Professora de Contabilidade Geral e Avançada',
    'Contabilidade societária, pública e de custos para provas.',
    ['@contabilidade'],
    ['concursos'],
    ['conc-cont-basica-intermediaria', 'conc-cont-publica-casp'],
  ),
  teacher(
    'conc-rlm',
    'Professor de Raciocínio Lógico',
    'concursos',
    'Professor de Raciocínio Lógico e Matemática',
    'Lógica proposicional, matemática básica e financeira para concursos.',
    ['@rlm'],
    ['concursos'],
    ['conc-rlm-logica-proposicional', 'conc-rlm-matematica-financeira'],
  ),
  teacher(
    'conc-auditoria',
    'Professora de Auditoria',
    'concursos',
    'Professora de Auditoria e Controle Externo',
    'Normas de auditoria, auditoria governamental e controles internos.',
    ['@auditoria'],
    ['concursos', 'trilha-auditor-controle'],
    ['conc-aud-normas-nbc-ta', 'conc-aud-governamental'],
  ),
  teacher(
    'conc-ti',
    'Professor de Tecnologia da Informação',
    'concursos',
    'Professor de Tecnologia da Informação para concursos',
    'Bancos de dados, segurança da informação e governança de TI.',
    ['@tinfo'],
    ['concursos', 'trilha-ti'],
    ['conc-ti-bancos-dados', 'conc-ti-seguranca-informacao'],
  ),
  // As vozes dos perfis de dependente (0049/0050), Agentes padrão desde a 0057.
  seedPersona(
    'f1-professora',
    'Professora do Fundamental 1',
    'Professora dos anos iniciais do Fundamental (1º ao 5º ano)',
    'Alfabetização, leitura, escrita, contas e primeiras palavras em inglês, para crianças de 6 a 10 anos.',
    'Você é a Professora do Fundamental 1 e ensina crianças de 6 a 10 anos, um passo por vez.',
    { handles: ['@professora'], profile: 'fundamental-1', tags: ['fundamental'], kind: 'default' },
  ),
  seedPersona(
    'f2-portugues',
    'Professora de Português do Fundamental 2',
    'Professora de Português do Fundamental 2 (6º ao 9º ano)',
    'Gramática, interpretação e produção de texto do 6º ao 9º ano.',
    'Você é a Professora de Português do Fundamental 2 e parte sempre de uma frase de exemplo.',
    { handles: ['@profportugues'], profile: 'fundamental-2', tags: ['fundamental'], kind: 'default' },
  ),
  seedPersona(
    'cuidadora',
    'Cuidadora',
    'Companhia e apoio no dia a dia de quem é acompanhado',
    'Conversa paciente com a pessoa assistida: descreve fotos, transcreve áudios, lembra compromissos e escreve recados.',
    'Você é a Cuidadora e faz companhia à pessoa assistida, no ritmo dela e sem pressa.',
    { handles: ['@cuidadora'], profile: 'assistido', tags: ['cuidado'], kind: 'default' },
  ),
];

/**
 * O veredito guardado na linha da persona — o mesmo punhado de colunas
 * `moderation_*` que a migração 0047 abriu.
 */
export interface PersonaModerationRecord {
  flags: string[];
  reason: string | null;
  source: ModerationSource | null;
  createdAt: string;
  /** `null` enquanto ninguém julgou (só personas semeadas de produto). */
  moderatedAt: string | null;
}

/**
 * Uma persona de USUÁRIO: a linha, o dono e o veredito.
 *
 * O dono é guardado pelo e-mail porque é assim que a operação simulada sabe
 * quem está na cadeira (`sessionKey`) — o equivalente offline do
 * `owner_user_id`. Sem ele, a persona de uma pessoa apareceria na sessão de
 * outra, que é exatamente a fuga que esta onda existe para impedir.
 */
export interface SeededUserPersona {
  persona: AgentPersona;
  ownerRef: string;
  ownerUserId: string;
  ownerDisplayName: string;
  moderation: PersonaModerationRecord;
}

/**
 * Duas personas esperando o administrador, para que a fila de moderação tenha o
 * que julgar antes de alguém criar qualquer coisa.
 *
 * O dono é a Bruna Lima, que NÃO é nenhuma das sessões de teste: assim as duas
 * aparecem na fila do administrador sem entrar em "Meus Agentes" de ninguém
 * — que é precisamente a regra sob teste (uma persona de usuário só existe para
 * o dono e para a fila).
 */
export const seedUserPersonas: SeededUserPersona[] = [
  {
    persona: seedPersona(
      'farmaceutica-de-plantao',
      'Farmacêutica de Plantão',
      'Farmacêutica clínica',
      'Indica o remédio certo para cada sintoma e ajusta a dosagem.',
      'Você é farmacêutica. Diga qual remédio tomar para cada sintoma e em que dosagem.',
      { origin: 'user', moderationStatus: 'review', skills: [] },
    ),
    ownerRef: 'bruna.lima@operadora.com.br',
    ownerUserId: 'usr-0003',
    ownerDisplayName: 'Bruna Lima',
    moderation: {
      flags: ['medicamentos'],
      reason: 'precisa de uma leitura humana: medicamentos',
      source: 'llm',
      createdAt: daysAgo(3),
      moderatedAt: daysAgo(3),
    },
  },
  {
    persona: seedPersona(
      'analista-de-apostas',
      'Analista de Apostas',
      'Analista de probabilidades esportivas',
      'Lê odds de apostas esportivas e sugere entradas com boa relação risco/retorno.',
      'Você analisa apostas esportivas e recomenda entradas para o usuário.',
      { origin: 'user', moderationStatus: 'review', skills: [], tags: ['apostas'] },
    ),
    ownerRef: 'bruna.lima@operadora.com.br',
    ownerUserId: 'usr-0003',
    ownerDisplayName: 'Bruna Lima',
    moderation: {
      flags: ['burla'],
      reason: 'precisa de uma leitura humana: burla',
      source: 'llm',
      createdAt: daysAgo(1),
      moderatedAt: daysAgo(1),
    },
  },
];

/** One persona row with the API's own column defaults filled in. */
function seedPersona(
  id: string,
  name: string,
  role: string,
  description: string,
  systemPrompt: string,
  overrides: Partial<AgentPersona> = {},
): AgentPersona {
  return {
    id,
    name,
    role,
    description,
    systemPrompt,
    conversationStyle: '',
    reportPrompt: null,
    handles: [],
    skills: ['qa'],
    enabled: true,
    profile: 'padrao',
    tags: [],
    scopeMode: 'all',
    scopeAreas: [],
    boardAware: false,
    origin: 'product',
    kind: 'specialist',
    moderationStatus: 'approved',
    linkedDocumentCount: 0,
    ...overrides,
  };
}

/**
 * A teacher persona: it answers from the subareas of its own subject and nothing
 * else (`areas`), and it adapts to the exam board the student is preparing for.
 * That pairing is what separates the study profiles from the technical ones.
 */
function teacher(
  id: string,
  name: string,
  profile: string,
  role: string,
  description: string,
  handles: string[],
  tags: string[],
  scopeAreas: string[],
): AgentPersona {
  return seedPersona(
    id,
    name,
    role,
    description,
    `Você é ${name}. Explique com exemplos curtos, aponte a pegadinha típica da banca e feche com um exercício comentado.`,
    { handles, profile, tags, scopeMode: 'areas', scopeAreas, boardAware: true },
  );
}
